import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Modal } from '@components/ui';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@lib/api/client';
import { useAuthStore } from '@features/auth/store/authStore';
import { useSupportHelp, type SupportStep } from './hooks/useSupportHelp';
import { SupportAssistantPanel } from './components/SupportAssistantPanel';
import { GuidedTour } from './components/GuidedTour';
import { SupportFeedbackToast } from './components/SupportFeedbackToast';

export interface ErrorDetail {
  code: string;
  message: string;
}

export interface ReportErrorArgs {
  title?: string;
  message: string;
  details?: ErrorDetail[];
  screen?: string; // pantalla/acción (ej. "crear cliente")
}

interface Ctx {
  reportError: (args: ReportErrorArgs) => void;
  // Muestra el toast "¿te funcionó?" en la esquina (persiste tras cerrar el modal).
  showFeedbackToast: (interactionId: string, topic?: string) => void;
  // Feature flags del proyecto (los prende/apaga el equipo desde el portal admin):
  // master del asistente + botón "Guíame en pantalla".
  assistantEnabled: boolean;
  guideEnabled: boolean;
}

const SupportErrorContext = createContext<Ctx>({
  reportError: () => {},
  showFeedbackToast: () => {},
  assistantEnabled: false,
  guideEnabled: false,
});

/** Llamá esto desde el onError/catch de una acción para mostrar el error + Asistente IA. */
export function useReportError() {
  return useContext(SupportErrorContext).reportError;
}

/** Para disparar el toast de feedback desde otros componentes (ej. SupportReportButton). */
export function useShowFeedbackToast() {
  return useContext(SupportErrorContext).showFeedbackToast;
}

/** Flags de features del asistente (para gatear botones en el portal). */
export function useSupportConfig() {
  const { assistantEnabled, guideEnabled } = useContext(SupportErrorContext);
  return { assistantEnabled, guideEnabled };
}

/**
 * Provee un modal de error único con "Asistente IA" inline. Cualquier feature lo
 * dispara con `reportError({...})` desde su onError → el agente analiza el error y
 * sugiere la solución. Opt-in (no se activa en búsquedas/validaciones).
 */
export function SupportErrorProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ReportErrorArgs & { open: boolean }>({ open: false, message: '' });
  // El tour vive acá (no dentro del modal) para que SOBREVIVA al cierre del modal.
  // Guardamos sessionId + goal para la guía iterativa (al cerrar el modal, el hook
  // se resetea, así que capturamos el contexto ACÁ).
  const [tour, setTour] = useState<{ steps: SupportStep[]; sessionId: string | null; goal: string } | null>(null);
  // Toast de feedback (vive acá → persiste aunque se cierre el modal).
  const [feedbackToast, setFeedbackToast] = useState<{ interactionId: string; topic?: string } | null>(null);

  // Feature flags del proyecto. Arrancan APAGADOS (fail-safe: nada aparece ni se
  // llama hasta confirmar que están activos). Se consultan al loguear.
  const { isAuthenticated } = useAuthStore();
  const [flags, setFlags] = useState({ assistant: false, guide: false });
  useEffect(() => {
    if (!isAuthenticated) {
      setFlags({ assistant: false, guide: false });
      return;
    }
    let alive = true;
    apiClient
      .get('/support/config')
      .then((r) => alive && setFlags({ assistant: r.data?.assistant_enabled !== false, guide: r.data?.guide_enabled !== false }))
      .catch(() => alive && setFlags({ assistant: false, guide: false }));
    return () => {
      alive = false;
    };
  }, [isAuthenticated]);

  const reportError = useCallback((args: ReportErrorArgs) => {
    setState({ ...args, open: true });
  }, []);

  const showFeedbackToast = useCallback((interactionId: string, topic?: string) => {
    setFeedbackToast({ interactionId, topic });
  }, []);

  const close = () => setState((s) => ({ ...s, open: false }));

  // Al pedir guía: capturamos contexto, cerramos el modal y arrancamos el tour.
  const startGuide = (steps: SupportStep[]) => {
    setTour({ steps, sessionId: help.sessionId, goal: state.message });
    setState((s) => ({ ...s, open: false }));
  };

  const help = useSupportHelp({
    // Si el asistente está apagado, NO se llama al agente (enabled=false).
    enabled: state.open && !!state.message && flags.assistant,
    message: [state.message, ...(state.details || []).map((d) => `${d.code}: ${d.message}`)].join('\n'),
    errorCodes: (state.details || []).map((d) => d.code).filter(Boolean),
    screen: state.screen || state.title,
  });

  // Apenas el agente responde, mostramos el toast de feedback (persiste aunque se
  // cierre el modal).
  useEffect(() => {
    if (flags.assistant && help.interactionId) showFeedbackToast(help.interactionId);
  }, [flags.assistant, help.interactionId, showFeedbackToast]);

  return (
    <SupportErrorContext.Provider value={{ reportError, showFeedbackToast, assistantEnabled: flags.assistant, guideEnabled: flags.guide }}>
      {children}
      <Modal isOpen={state.open} onClose={close} title={state.title || 'Ocurrió un error'} size="lg">
        <div className="space-y-4" data-support-ui>
          <div className="flex items-start space-x-3">
            <XCircleIcon className="h-8 w-8 text-danger-500 flex-shrink-0" />
            <p className="text-sm text-secondary-700 dark:text-secondary-300 flex-1">{state.message}</p>
          </div>

          {state.details && state.details.length > 0 && (
            <div className="space-y-2">
              {state.details.map((d, i) => (
                <div key={i} className="bg-danger-50 dark:bg-danger-900/20 border border-danger-200 dark:border-danger-800 rounded-lg p-3 flex items-start space-x-2">
                  {d.code && (
                    <span className="inline-flex items-center justify-center h-5 px-1.5 rounded-full bg-danger-100 dark:bg-danger-900/40 text-danger-800 dark:text-danger-200 text-xs font-medium flex-shrink-0 mt-0.5">
                      {d.code}
                    </span>
                  )}
                  <p className="text-sm text-danger-800 dark:text-danger-200 flex-1">{d.message}</p>
                </div>
              ))}
            </div>
          )}

          {flags.assistant && <SupportAssistantPanel help={help} onGuide={startGuide} guideEnabled={flags.guide} />}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={close}
              className="inline-flex justify-center items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-danger-600 hover:bg-danger-700 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      </Modal>

      {/* Recorrido guiado sobre el portal real (fuera del modal, para que persista). */}
      {tour && tour.steps.length > 0 && (
        <GuidedTour
          steps={tour.steps}
          sessionId={tour.sessionId ?? undefined}
          goal={tour.goal}
          onComplete={(journey) =>
            apiClient
              .post('/support/guide-complete', { sessionId: tour.sessionId, goal: tour.goal, journey })
              .catch(() => {})
          }
          onClose={() => setTour(null)}
        />
      )}

      {/* Toast "¿te funcionó?" en la esquina (persiste tras cerrar el modal). */}
      {feedbackToast && (
        <SupportFeedbackToast
          interactionId={feedbackToast.interactionId}
          topic={feedbackToast.topic}
          onClose={() => setFeedbackToast(null)}
        />
      )}
    </SupportErrorContext.Provider>
  );
}
